/**
 * BifrostWeb - Main RPC framework class
 * TypeScript-first bidirectional WebView RPC framework
 */

import {
  RpcEnvelope,
  RpcResponse,
  BifrostConfig,
  CallOptions,
  ServiceDescriptor,
  Interceptor,
  DEFAULT_READY_TIMEOUT_MS,
  V2BootstrapAdapter,
} from "./types/protocol";
import {
  CallCancelledError,
  CallDeadlineExceededError,
  ErrorCode,
  RpcCallError,
  TransportNotAdmittedError,
} from "./types/errors";
import { ConnectionState } from "./core/ConnectionState";
import { RetryManager } from "./core/RetryManager";
import { ServiceRegistry } from "./core/ServiceRegistry";
import { NativeProxyManager, ProxyOptions } from "./core/NativeProxyManager";
import { BifrostBridge, IBifrostClient } from "./core/BifrostBridge";
import { InterceptorChain } from "./core/InterceptorChain";
import { ProxyBuilder } from "./core/ProxyBuilder";
import { assertValidChunkConfig, ChunkConfig } from "./core/ChunkSender";
import { CALL_RETRY_BOUNDARY } from "./core/CallRetryBoundary";
import {
  decodeResourceRepresentation,
  ResourceRepresentation,
  ResourceRequest,
  validateResourceRequest,
} from "./types/resources";

// Global handler function name that native will call
declare global {
  interface Window {
    bifrostHandleN2W: (message: any) => void;
    bifrostNativeBridge?: {
      sendToNative: (envelope: RpcEnvelope) => void;
      sendSerializedToNative?: (json: string) => void;
      maxUnchunkedMessageBytes?: number;
      onWebReady: ((handshakeId?: string) => void) | undefined;
      isReady: boolean;
    };
    bifrostV2Bootstrap?: V2BootstrapAdapter;
  }
}

// Remove PendingCall interface - now handled by BifrostBridge

interface NativeProxy {
  call: (method: string, payload?: any, options?: CallOptions) => Promise<any>;
  notify: (method: string, payload?: any) => void;
}

/**
 * BifrostWeb - Main RPC framework class for bidirectional WebView communication
 *
 * Enables seamless communication between web content and native applications
 * with full TypeScript support, error handling, and advanced features like
 * interceptors, and connection management.
 *
 * @example
 * ```typescript
 * const bifrost = new BifrostWeb({
 *   readyTimeout: 15000,
 *   maxInflightCalls: 32
 * });
 *
 * await bifrost.initialize();
 * const result = await bifrost.call('UserService', 'getProfile', { userId: 123 });
 * ```
 */
export class BifrostWeb implements IBifrostClient {
  /** Reserved native service used by the explicit V2 resource API. */
  static readonly RESOURCE_SERVICE = "BifrostResourceService";
  // Instance properties
  readonly clientId: string;

  private config: BifrostConfig & {
    readyTimeout: number;
    maxInflightCalls: number;
  };
  private defaultCallOptions?: CallOptions;
  private connectionState: ConnectionState;
  private retryManager: RetryManager;
  private serviceRegistry: ServiceRegistry;
  private proxyManager: NativeProxyManager;
  private interceptorChain: InterceptorChain;
  private chunkConfig: ChunkConfig = {};

  private callIdCounter = 0;
  private readonly lifetime = new AbortController();
  public isDestroyed = false;

  /**
   * Creates a new BifrostWeb instance
   *
   * @param config - Configuration options for the RPC framework
   * @param config.readyTimeout - Deadline for the native handshake. Defaults to
   * DEFAULT_READY_TIMEOUT_MS, which is sized to the host's own page deadline.
   * @param config.maxInflightCalls - Maximum concurrent RPC calls (default: 32)
   * @param config.retryConfig - Retry configuration for failed calls.
   * `retryConfig.retryableErrors` is ignored: a native error response means
   * the call was already admitted and may have changed state, so this
   * client never replays it on the strength of an error class.
   * @param config.enableDebug - Enable debug logging (default: false)
   * @param config.interceptors - Default interceptors to register
   * @param config.onReady - Callback when native bridge is ready
   * @param config.onError - Callback for error handling
   */
  constructor(config: BifrostConfig = {}) {
    // Generate unique client ID
    this.clientId = this.generateClientId();

    // Set default configuration
    this.config = {
      readyTimeout: config.readyTimeout ?? DEFAULT_READY_TIMEOUT_MS,
      maxInflightCalls: config.maxInflightCalls ?? 32,
      retryConfig: config.retryConfig ?? {
        maxRetries: 3,
        retryDelay: 1000,
        retryBackoff: 2,
      },
      enableDebug: config.enableDebug ?? false,
      onReady: config.onReady,
      onError: config.onError,
    };

    // Store global default call options
    this.defaultCallOptions = config.defaultCallOptions;

    // Initialize core components
    this.connectionState = new ConnectionState(this.config.readyTimeout);
    this.retryManager = new RetryManager({
      maxRetries: this.config.retryConfig?.maxRetries ?? 3,
      retryDelay: this.config.retryConfig?.retryDelay ?? 1000,
      retryBackoff: this.config.retryConfig?.retryBackoff ?? 2,
      // A native response means the operation was admitted and may already
      // have changed state. Bifrost calls only replay explicit refusals.
      retryableErrors: [],
    });
    this.serviceRegistry = new ServiceRegistry();
    this.proxyManager = new NativeProxyManager(this);
    this.interceptorChain = new InterceptorChain();

    // Register default interceptors from config
    if (config.interceptors) {
      for (const interceptor of config.interceptors) {
        this.interceptorChain.addInterceptor(interceptor);
      }
    }
  }

  /**
   * Get fresh bridge instance (handles reconnection after destruction)
   */
  private getBridge(): BifrostBridge {
    return BifrostBridge.getInstance();
  }

  /**
   * Initialize the BifrostWeb framework
   *
   * Sets up the connection to the native bridge, configures message handlers,
   * and waits for the native side to be ready for communication.
   *
   * @throws {Error} If native bridge is not available or ready timeout is exceeded
   *
   * @example
   * ```typescript
   * const bifrost = new BifrostWeb();
   * await bifrost.initialize();
   * // Now ready to make RPC calls
   * ```
   */
  async initialize(): Promise<void> {
    if (this.isDestroyed) {
      throw new Error("BifrostWeb has been destroyed");
    }

    // Get bridge and register this client
    const bridge = this.getBridge();
    bridge.registerClient(this);

    // Initialize the bridge (handles native bridge setup and global handler)
    await bridge.initialize(this.config.readyTimeout);
    this.connectionState.setReady();
  }

  /**
   * The shared bridge handshake completed (see IBifrostClient.markBridgeReady).
   */
  markBridgeReady(): void {
    if (!this.isDestroyed) {
      this.connectionState.setReady();
    }
  }

  /**
   * Register a service for native-to-web calls
   *
   * Registers a JavaScript object as a service that can be called from the native side.
   * All methods on the service object become callable via RPC.
   *
   * @param serviceName - Unique name for the service
   * @param serviceImpl - Object containing methods to expose to native side
   *
   * @throws {Error} If service name is already registered or invalid
   *
   * @example
   * ```typescript
   * const webService = {
   *   showNotification: (message: string) => {
   *     new Notification(message);
   *     return { displayed: true };
   *   },
   *   validateForm: async (data: any) => {
   *     return { valid: true, errors: [] };
   *   }
   * };
   *
   * bifrost.registerService('WebUIService', webService);
   * ```
   */
  registerService(serviceName: string, serviceImpl: any): void {
    this.throwIfDestroyed();
    this.serviceRegistry.register(serviceName, serviceImpl);
    // Keep the simple broadcast behavior for now - no need to register with bridge
  }

  /**
   * Unregister a service
   */
  unregisterService(serviceName: string): void {
    this.throwIfDestroyed();
    this.serviceRegistry.unregister(serviceName);
  }

  /**
   * Check if this client has a specific service (required by IBifrostClient)
   */
  hasService(serviceName: string): boolean {
    return this.serviceRegistry.listServices().includes(serviceName);
  }

  /**
   * Call a native method with safe transport retry logic
   *
   * Sends an RPC call to the native side and waits for the response.
   * Retries calls explicitly refused before admission and handles timeouts.
   *
   * @param service - Name of the native service to call
   * @param method - Method name on the service
   * @param payload - Parameters to send with the call (default: {})
   * @param options - Call options including timeout and retries
   * @returns Promise that resolves with the method result
   *
   * @throws {RpcCallError} If the call fails or times out
   *
   * @example
   * ```typescript
   * // Simple call
   * const result = await bifrost.call('UserService', 'getProfile', { userId: 123 });
   *
   * // Call with options
   * const result = await bifrost.call('FileService', 'upload', { name: 'file.pdf' }, {
   *   timeout: 30000,
   * });
   * ```
   */
  async call(
    service: string,
    method: string,
    payload: any = {},
    options?: CallOptions,
  ): Promise<any> {
    this.throwIfDestroyed();

    // Merge global + call-level options (no proxy/method involved in direct calls)
    const finalOptions = this.mergeCallOptions(undefined, undefined, options);
    const deadlineAt = Date.now() + (finalOptions?.timeout ?? 5000);
    await this.waitUntilReady(deadlineAt, finalOptions?.signal);
    await this.getBridge().recoverV2(deadlineAt);
    const release = await this.getBridge().admitCall(
      deadlineAt,
      this.config.maxInflightCalls,
      finalOptions?.signal,
    );
    const applicationCallId = this.generateCallId();
    try {
      return await this.retryManager.withRetry(async () => {
        return this.performCall(
          service,
          method,
          payload,
          finalOptions,
          applicationCallId,
          deadlineAt,
        );
      });
    } finally {
      release();
    }
  }

  /**
   * Request one native resource using an explicit representation preference.
   *
   * The returned value is always the tagged V2 union. Legacy callers should
   * continue using their existing untagged Base64 service contracts.
   */
  async requestResource(
    request: ResourceRequest,
    options?: CallOptions,
  ): Promise<ResourceRepresentation> {
    const validated = validateResourceRequest(request);
    const result = await this.call(
      BifrostWeb.RESOURCE_SERVICE,
      "resolve",
      validated,
      options,
    );
    return decodeResourceRepresentation(result);
  }

  /**
   * Send notification to native (fire-and-forget)
   */
  notify(service: string, method: string, payload: any = {}): void {
    this.throwIfDestroyed();

    const callId = this.generateCallId();

    const envelope: RpcEnvelope = {
      id: callId,
      kind: "notify",
      service,
      method,
      payload,
      ts: Date.now(),
    };
    const chunkConfig = this.chunkConfig;

    // Process through outbound interceptor chain (async but not awaited for fire-and-forget)
    this.interceptorChain
      .process("outbound", envelope, async () => {
        this.getBridge().sendNotification(
          service,
          method,
          payload,
          callId,
          chunkConfig,
        );
        return undefined;
      })
      .catch((error) => {
        // Log interceptor errors but don't throw since notify is fire-and-forget
        if (this.config.enableDebug) {
          console.error("Interceptor error in notify:", error); // eslint-disable-line no-console
        }
      });
  }

  /**
   * Create a typed native service proxy with caching and validation
   *
   * Creates a strongly-typed proxy object that provides direct method access
   * to native services. Method calls are automatically converted to RPC calls
   * with full TypeScript support and IntelliSense.
   *
   * @template T - TypeScript interface defining the service methods
   * @param serviceName - Name of the native service to proxy
   * @param options - Proxy configuration options
   * @returns Typed proxy object with methods that call the native service
   *
   * @example
   * ```typescript
   * interface UserService {
   *   getProfile(userId: number): Promise<UserProfile>;
   *   updateProfile(data: Partial<UserProfile>): Promise<void>;
   * }
   *
   * const userService = bifrost.createNativeProxy<UserService>('UserService');
   * const profile = await userService.getProfile(123);
   * await userService.updateProfile({ name: 'New Name' });
   * ```
   */
  createNativeProxy<T = any>(serviceName: string, options?: ProxyOptions): T {
    this.throwIfDestroyed();
    return this.proxyManager.createProxy<T>(serviceName, options);
  }

  /**
   * Create a ProxyBuilder for configuring per-method call options
   *
   * Provides a fluent API for configuring different CallOptions (timeout, maxRetries)
   * for individual methods on a proxy. Supports four-tier option precedence:
   * global < proxy < method < call.
   *
   * @template T - TypeScript interface defining the service methods
   * @param serviceName - Name of the native service to proxy
   * @returns ProxyBuilder instance for configuring the proxy
   *
   * @example
   * ```typescript
   * interface UserService {
   *   quickCheck(): Promise<boolean>;
   *   generateReport(params: any): Promise<Report>;
   * }
   *
   * const userService = bifrost
   *   .createProxyBuilder<UserService>('UserService')
   *   .withDefaultOptions({ timeout: 5000 })
   *   .configureMethod('generateReport', { timeout: 60000, maxRetries: 1 })
   *   .build();
   *
   * const isReady = await userService.quickCheck(); // Uses 5000ms timeout
   * const report = await userService.generateReport({}); // Uses 60000ms timeout
   * ```
   */
  createProxyBuilder<T = any>(serviceName: string): ProxyBuilder<T> {
    this.throwIfDestroyed();
    return new ProxyBuilder<T>(this, serviceName);
  }

  /**
   * Create a native service proxy (legacy method for backward compatibility)
   */
  createLegacyNativeProxy(serviceName: string): NativeProxy {
    this.throwIfDestroyed();

    return {
      call: (method: string, payload?: any, options?: CallOptions) => {
        return this.call(serviceName, method, payload, options);
      },
      notify: (method: string, payload?: any) => {
        this.notify(serviceName, method, payload);
      },
    };
  }

  /**
   * List all registered services
   */
  listServices(): string[] {
    this.throwIfDestroyed();
    return this.serviceRegistry.listServices();
  }

  /**
   * Get service descriptor
   */
  getServiceDescriptor(serviceName: string): ServiceDescriptor {
    this.throwIfDestroyed();
    return this.serviceRegistry.getServiceDescriptor(serviceName);
  }

  /**
   * List all cached native proxies
   */
  listCachedProxies(): string[] {
    this.throwIfDestroyed();
    return this.proxyManager.listCachedProxies();
  }

  /**
   * Clear specific proxy from cache
   */
  clearProxyCache(serviceName: string): void {
    this.throwIfDestroyed();
    this.proxyManager.clearProxy(serviceName);
  }

  /**
   * Clear all proxy caches
   */
  clearAllProxyCaches(): void {
    this.throwIfDestroyed();
    this.proxyManager.clearAllProxies();
  }

  /**
   * Add an interceptor to the chain
   *
   * Interceptors are executed in order based on their `order` property.
   * Lower order values execute first.
   *
   * @param interceptor - The interceptor to add
   *
   * @example
   * ```typescript
   * import { LoggingInterceptor } from '@seekrtech/bifrost-web';
   *
   * const loggingInterceptor = new LoggingInterceptor();
   * bifrost.addInterceptor(loggingInterceptor);
   * ```
   */
  addInterceptor(interceptor: Interceptor): void {
    this.throwIfDestroyed();
    this.interceptorChain.addInterceptor(interceptor);
  }

  /**
   * Remove an interceptor from the chain by name
   *
   * @param name - Name of the interceptor to remove
   *
   * @example
   * ```typescript
   * bifrost.removeInterceptor('LoggingInterceptor');
   * ```
   */
  removeInterceptor(name: string): void {
    this.throwIfDestroyed();
    this.interceptorChain.removeInterceptor(name);
  }

  /**
   * Configure chunking thresholds for large message transport
   */
  setChunkConfig(config: ChunkConfig): void {
    // Every other public method guards this; without it, calling setChunkConfig
    // on a destroyed client resurrects the bridge singleton and reinstalls the
    // global message handler.
    this.throwIfDestroyed();
    const nextConfig = { ...this.chunkConfig, ...config };
    assertValidChunkConfig(nextConfig);
    this.chunkConfig = nextConfig;
  }

  /**
   * Clear all interceptors from the chain
   */
  clearInterceptors(): void {
    this.throwIfDestroyed();
    this.interceptorChain.clearInterceptors();
  }

  /**
   * Get all registered interceptors
   */
  getInterceptors(): Interceptor[] {
    this.throwIfDestroyed();
    return this.interceptorChain.getInterceptors();
  }

  /**
   * Get global default call options (used by NativeProxyManager)
   *
   * @returns Global default CallOptions or undefined
   */
  getDefaultCallOptions(): CallOptions | undefined {
    return this.defaultCallOptions;
  }

  /**
   * Merge call options from multiple levels
   * Priority: global < proxy < method < call (rightmost wins)
   *
   * @param proxyOptions - Proxy-level default options
   * @param methodOptions - Method-specific options
   * @param callOptions - Call-level options
   * @returns Merged CallOptions or undefined if all inputs are undefined
   */
  mergeCallOptions(
    proxyOptions?: CallOptions,
    methodOptions?: CallOptions,
    callOptions?: CallOptions,
  ): CallOptions | undefined {
    const merged = {
      ...this.defaultCallOptions,
      ...proxyOptions,
      ...methodOptions,
      ...callOptions,
    };

    // Return undefined if result is empty object (preserves backward compatibility)
    return Object.keys(merged).length === 0 ? undefined : merged;
  }

  /**
   * Destroy the framework and cleanup resources
   */
  destroy(): void {
    if (this.isDestroyed) {
      return;
    }

    this.lifetime.abort();

    // Unregister from bridge (if bridge still exists)
    try {
      this.getBridge().unregisterClient(this.clientId);
    } catch (error) {
      // Bridge might be destroyed already, that's okay
    }

    // Cleanup proxy manager
    this.proxyManager.destroy();

    this.isDestroyed = true;
  }

  /**
   * Perform actual RPC call
   */
  private async performCall(
    service: string,
    method: string,
    payload: any,
    options?: CallOptions,
    applicationCallId?: string,
    deadlineAt?: number,
  ): Promise<any> {
    const callDeadlineAt =
      deadlineAt ?? Date.now() + (options?.timeout ?? 5000);
    this.assertCallActive(callDeadlineAt, options?.signal);
    await this.connectionState.ensureReady();
    this.assertCallActive(callDeadlineAt, options?.signal);

    const callId = this.generateCallId();

    const envelope: RpcEnvelope = {
      id: callId,
      kind: "call",
      service,
      method,
      payload,
      ts: Date.now(),
      applicationCallId,
      idempotencyKey: options?.idempotencyKey,
      deadlineAt: callDeadlineAt,
    };
    const chunkConfig = this.chunkConfig;

    // Process through outbound interceptor chain
    return this.interceptorChain.process("outbound", envelope, async () => {
      this.assertCallActive(callDeadlineAt, options?.signal);
      const remainingOptions =
        options?.timeout !== undefined
          ? { ...options, timeout: Math.max(0, callDeadlineAt - Date.now()) }
          : options;
      // Use bridge to send call with the (potentially modified) envelope
      try {
        return await this.getBridge().sendCall(
          this.clientId,
          envelope.id,
          envelope.service!,
          envelope.method!,
          envelope.payload,
          remainingOptions,
          envelope,
          chunkConfig,
        );
      } catch (error) {
        if (error instanceof TransportNotAdmittedError) {
          // Deliberate: tag the error with a call-scoped retry boundary so
          // RetryManager can wait on this call's own deadline/signal instead
          // of a plain sleep, without either module importing the other.
          Object.defineProperty(error, CALL_RETRY_BOUNDARY, {
            value: {
              waitBeforeRetry: (delay: number) =>
                this.waitBeforeCallRetry(
                  delay,
                  callDeadlineAt,
                  options?.signal,
                ),
            },
          });
        }
        throw error;
      }
    });
  }

  private assertCallActive(deadlineAt: number, signal?: AbortSignal): void {
    this.throwIfDestroyed();
    if (this.lifetime.signal.aborted)
      throw new Error("BifrostWeb has been destroyed");
    if (signal?.aborted) throw new CallCancelledError();
    if (Date.now() >= deadlineAt) throw new CallDeadlineExceededError();
  }

  private waitBeforeCallRetry(
    delay: number,
    deadlineAt: number,
    signal?: AbortSignal,
  ): Promise<void> {
    this.assertCallActive(deadlineAt, signal);
    return new Promise((resolve, reject) => {
      const remaining = Math.max(0, deadlineAt - Date.now());
      const timer = setTimeout(() => {
        try {
          this.assertCallActive(deadlineAt, signal);
          finish(resolve);
        } catch (error) {
          finish(() => reject(error));
        }
      }, Math.min(delay, remaining));
      const cancel = () => finish(() => reject(new CallCancelledError()));
      const dispose = () =>
        finish(() => reject(new Error("BifrostWeb has been destroyed")));
      const finish = (settle: () => void) => {
        clearTimeout(timer);
        signal?.removeEventListener("abort", cancel);
        this.lifetime.signal.removeEventListener("abort", dispose);
        settle();
      };
      signal?.addEventListener("abort", cancel, { once: true });
      this.lifetime.signal.addEventListener("abort", dispose, { once: true });
    });
  }

  private async waitUntilReady(
    deadlineAt: number,
    signal?: AbortSignal,
  ): Promise<void> {
    if (signal?.aborted) throw new CallCancelledError();
    await new Promise<void>((resolve, reject) => {
      const timer = setTimeout(
        () => finish(() => reject(new CallDeadlineExceededError())),
        Math.max(0, deadlineAt - Date.now()),
      );
      const abort = () => finish(() => reject(new CallCancelledError()));
      const finish = (settle: () => void) => {
        clearTimeout(timer);
        signal?.removeEventListener("abort", abort);
        settle();
      };
      signal?.addEventListener("abort", abort, { once: true });
      this.connectionState.ensureReady().then(
        () => finish(resolve),
        (error) => finish(() => reject(error)),
      );
    });
  }

  /**
   * Handle incoming call from native (required by IBifrostClient)
   */
  async handleIncomingCall(envelope: RpcEnvelope): Promise<void> {
    // Process through inbound interceptor chain
    await this.interceptorChain.process("inbound", envelope, async () => {
      let result: any = null;
      let error: any = null;

      try {
        result = await this.serviceRegistry.invoke(
          envelope.service!,
          envelope.method!,
          envelope.payload,
        );
      } catch (err) {
        if (err instanceof RpcCallError) {
          error = {
            code: err.code,
            message: err.message,
          };
        } else {
          error = {
            code: ErrorCode.INTERNAL_ERROR,
            message: err instanceof Error ? err.message : "Unknown error",
          };
        }
      }

      // Create response
      const response: RpcResponse = {
        id: envelope.id,
        ok: error === null,
        result,
        error,
        ts: Date.now(),
      };
      const chunkConfig = this.chunkConfig;

      // Process response through outbound interceptor chain
      await this.interceptorChain.process("outbound", response, async () => {
        // Use bridge to send response back
        this.getBridge().sendResponse(response, chunkConfig);
        return undefined;
      });

      return undefined;
    });
  }

  /**
   * Handle incoming notification from native (required by IBifrostClient)
   */
  handleIncomingNotification(envelope: RpcEnvelope): void {
    // Process through inbound interceptor chain (async but not awaited for fire-and-forget)
    this.interceptorChain
      .process("inbound", envelope, async () => {
        await this.serviceRegistry.invoke(
          envelope.service!,
          envelope.method!,
          envelope.payload,
        );
      })
      .catch((error) => {
        // Log interceptor errors but don't throw since notifications are fire-and-forget
        if (this.config.enableDebug) {
          console.error(
            "Interceptor error in handleIncomingNotification:",
            error,
          ); // eslint-disable-line no-console
        }
      });
  }

  /**
   * Generate unique client ID
   */
  private generateClientId(): string {
    return `client_${Math.random().toString(36).substring(2)}_${Date.now()}`;
  }

  /**
   * Generate unique call ID
   */
  private generateCallId(): string {
    return `${this.clientId}_${++this.callIdCounter}_${Date.now()}`;
  }

  /**
   * Throw error if framework has been destroyed
   */
  private throwIfDestroyed(): void {
    if (this.isDestroyed) {
      throw new Error("BifrostWeb has been destroyed");
    }
  }
}
